import { v4 as uuidv4 } from "uuid";

import { OrganisationTypeConstants, ProjectAccessConstants } from "../../../../../constants";
import { AddedOrganisationAssignment, ProjectRollupRow, ProjectRow, SelectData } from "../../../../../models";
import { BulkInviteOrganisationsToProjectsRequestAssignment } from "../../../../../service/organisation";
import { ServiceError } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";

// One planned invite, kept next to the names it came from so an Assignments[index] error can be
// reported against the organisation and project the user actually chose.
export type PlannedInvite = {
  assignment: BulkInviteOrganisationsToProjectsRequestAssignment;
  organisationName: string;
  projectName: string;
};

export const projectDisplayName = (row: ProjectRow, index: number): string => row.displayName || `Project ${index + 1}`;

const organisationsForProject = (
  organisations: AddedOrganisationAssignment[],
  projectKey: string
): AddedOrganisationAssignment[] =>
  organisations.filter((organisation) => organisation.projectKeys.includes(projectKey));

// REQ-003 - a project the user has not touched is owned by the creating organisation.
export const getResponsibleOwnerUuid = (
  projectKey: string,
  responsibleOwners: Record<string, string>,
  creatingOrganisationUuid: string
): string => responsibleOwners[projectKey] || creatingOrganisationUuid;

// Two rows never get a remove control: the project's full-access Developer, which a project cannot
// be left without (BR-02726), and the creating organisation while it still holds its own full-access
// role - the create writes that link whatever this list says, so removing it here would be a lie.
// A creator a swap has downgraded to a guest role is removable: that records REMOVE on the swap.
const canRemoveFromProject = (organisation: AddedOrganisationAssignment, creatingOrganisationUuid: string): boolean =>
  organisation.role !== OrganisationTypeConstants.DEVELOPER &&
  !(
    organisation.organisationUuid === creatingOrganisationUuid &&
    ProjectAccessConstants.canRoleBeResponsibleOwner(organisation.role)
  );

export const buildRollupRows = (
  rows: ProjectRow[],
  organisations: AddedOrganisationAssignment[],
  responsibleOwners: Record<string, string>,
  creatingOrganisationUuid: string,
  landholdingNameByRowKey: Record<string, string> = {}
): ProjectRollupRow[] =>
  rows.map((row, index) => ({
    projectKey: row.key,
    projectName: projectDisplayName(row, index),
    landholdingName: landholdingNameByRowKey[row.key] ?? null,
    organisations: organisationsForProject(organisations, row.key).map((organisation) => ({
      organisationUuid: organisation.organisationUuid,
      organisationName: organisation.organisationName,
      organisationType: organisation.organisationType,
      access: organisation.accessLabel,
      canRemove: canRemoveFromProject(organisation, creatingOrganisationUuid),
    })),
    responsibleOwnerOrganisationUuid: getResponsibleOwnerUuid(row.key, responsibleOwners, creatingOrganisationUuid),
  }));

// One project's current full-access developer, if it has one - there can be at most one (BR-02878),
// so whichever organisation's entry carries the role is the answer, the creator's own default entry
// included.
const getCurrentFullDeveloper = (
  organisations: AddedOrganisationAssignment[],
  projectKey: string
): AddedOrganisationAssignment | undefined =>
  organisationsForProject(organisations, projectKey).find(
    (organisation) => organisation.role === OrganisationTypeConstants.DEVELOPER
  );

// The developer a project has to be created with (createProject's developerUuid): the organisation
// the access list gives full access to as Developer, named by its developer record rather than its
// parent organisation.
//
// Null when that is the creating organisation, because the api reads who is creating the project
// from the caller and records them itself - naming them again would say nothing it does not
// already know. Null too when the list has no developer at all, a Development Manager's create
// until one is chosen, which creates the project without one.
export const getProjectDeveloperUuid = (
  organisations: AddedOrganisationAssignment[],
  projectKey: string,
  creatingOrganisationUuid: string
): string | null => {
  const developer = getCurrentFullDeveloper(organisations, projectKey);

  if (!developer || developer.organisationUuid === creatingOrganisationUuid) return null;

  return developer.subOrganisationUuid ?? null;
};

export const creatingOrganisationKey = (role: string): string => `creating-organisation-${role}`;

/** The creating organisation as the access list needs to know it. */
export type CreatingOrganisationEntry = {
  uuid: string;
  name: string;
  organisationType: string;
  /** The full-access role its type carries: what the api records for it at create (REQ-003). */
  role: string;
};

// What an existingDeveloperAction leaves the developer it displaces holding - null where it takes
// them off the project altogether.
const roleAfterExistingDeveloperAction = (action: string): string | null => {
  switch (action) {
    case "REMOVE":
      return null;
    case "DOWNGRADE_GUEST_READ":
      return ProjectAccessConstants.GUEST_READ_ROLE;
    case "DOWNGRADE_GUEST_WRITE":
      return ProjectAccessConstants.GUEST_WRITE_ROLE;
    default:
      return OrganisationTypeConstants.DEVELOPER;
  }
};

// A full-access role reads as "Full access", whatever type carries it; a guest role is its own label.
const accessLabelForRole = (role: string): string =>
  ProjectAccessConstants.canRoleBeResponsibleOwner(role) ? ProjectAccessConstants.FULL_ACCESS_LABEL : role;

// What role, if any, the creator's own entry holds on one project: its type's full-access role by
// default (REQ-003), or - for a developer creator that a Developer add has displaced - whatever that
// add's existingDeveloperAction downgraded it to, or none at all for REMOVE. Read back from the other
// organisation's own entry rather than tracked separately, so there is nothing here to fall out of
// sync with it. Only a Developer can be displaced, so any other creator always keeps its role.
const resolveCreatorRole = (
  projectKey: string,
  otherOrganisations: AddedOrganisationAssignment[],
  creator: CreatingOrganisationEntry
): string | null => {
  const swapAction = otherOrganisations.find(
    (organisation) =>
      organisation.projectKeys.includes(projectKey) && !!organisation.existingDeveloperActions?.[projectKey]
  )?.existingDeveloperActions?.[projectKey];

  if (creator.role !== OrganisationTypeConstants.DEVELOPER || !swapAction) return creator.role;

  return roleAfterExistingDeveloperAction(swapAction);
};

const serializeCreatorAssignments = (assignments: AddedOrganisationAssignment[]): string =>
  JSON.stringify(
    [...assignments]
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(({ key, organisationName, projectKeys }) => ({ key, organisationName, projectKeys }))
  );

// The creating organisation is on every project it creates without ever sending itself an invite:
// the create writes its project_organisation link with its type's full-access role (REQ-003). It is
// kept as a real access.organisations entry - grouped by role, since a swap can downgrade a developer
// creator on one project while it stays Full access on another - rather than inferred, so the table
// lists it and BR-02878's one-developer check sees it like any other organisation's. Reconciled from
// the rows and every other organisation's own existingDeveloperAction each time either changes, so
// a REMOVE or a downgrade is read back from where it was recorded rather than remembered twice.
// buildPlannedInvites leaves it out of the invitations, which is where "omitted" belongs.
export const syncCreatingOrganisation = (
  organisations: AddedOrganisationAssignment[],
  rows: ProjectRow[],
  creator: CreatingOrganisationEntry
): AddedOrganisationAssignment[] => {
  const others = organisations.filter((organisation) => organisation.organisationUuid !== creator.uuid);
  const current = organisations.filter((organisation) => organisation.organisationUuid === creator.uuid);

  const projectKeysByRole = new Map<string, string[]>();
  rows.forEach((row) => {
    const role = resolveCreatorRole(row.key, others, creator);
    if (!role) return;
    projectKeysByRole.set(role, [...(projectKeysByRole.get(role) ?? []), row.key]);
  });

  const next: AddedOrganisationAssignment[] = Array.from(projectKeysByRole.entries()).map(([role, projectKeys]) => ({
    key: creatingOrganisationKey(role),
    organisationUuid: creator.uuid,
    organisationName: creator.name,
    organisationType: creator.organisationType,
    role,
    accessLabel: accessLabelForRole(role),
    projectKeys,
  }));

  if (serializeCreatorAssignments(next) === serializeCreatorAssignments(current)) {
    return organisations;
  }

  return [...next, ...others];
};

/**
 * Only the organisations that hold access on this project, and only those whose role can carry the
 * designation - a guest cannot (BR-02871), and neither can an organisation a swap has taken off the
 * project altogether. The creator is listed from its own entry (syncCreatingOrganisation) like any
 * other, and drops out when a swap removes it.
 */
export const buildResponsibleOwnerOptions = (
  projectKey: string,
  organisations: AddedOrganisationAssignment[]
): SelectData => {
  const seen = new Set<string>();

  return organisationsForProject(organisations, projectKey)
    .filter((organisation) => ProjectAccessConstants.canRoleBeResponsibleOwner(organisation.role))
    .filter((organisation) => {
      if (seen.has(organisation.organisationUuid)) return false;
      seen.add(organisation.organisationUuid);
      return true;
    })
    .map((organisation) => ({ key: organisation.organisationUuid, value: organisation.organisationName }));
};

export type ProjectAccessState = {
  organisations: AddedOrganisationAssignment[];
  responsibleOwners: Record<string, string>;
};

// One project taken off an organisation's entry, and the entry itself once it covers none.
const withoutProjectKey = (
  organisation: AddedOrganisationAssignment,
  projectKey: string
): AddedOrganisationAssignment[] => {
  const projectKeys = organisation.projectKeys.filter((key) => key !== projectKey);

  if (projectKeys.length === 0) return [];

  return [
    {
      ...organisation,
      projectKeys,
      existingDeveloperActions: Object.fromEntries(
        Object.entries(organisation.existingDeveloperActions ?? {}).filter(([key]) => key !== projectKey)
      ),
    },
  ];
};

/**
 * Takes one organisation off one project, leaving its other projects alone.
 *
 * The project's full-access Developer is refused: a project cannot be left without one (BR-02726),
 * and replacing it is what the add card's swap is for. Everyone else is an ordinary invite that has
 * not been sent, so it is simply dropped - except the creating organisation, whose rows are derived
 * from the swap that downgraded it (syncCreatingOrganisation) and would come straight back. Removing
 * that one is recorded as REMOVE on the organisation whose add displaced it, which is the same
 * thing said where the api will read it.
 */
export const removeOrganisationFromProject = (
  access: ProjectAccessState,
  projectKey: string,
  organisationUuid: string,
  creatingOrganisationUuid: string
): ProjectAccessState => {
  const removed = organisationsForProject(access.organisations, projectKey).find(
    (organisation) => organisation.organisationUuid === organisationUuid
  );

  if (!removed || removed.role === OrganisationTypeConstants.DEVELOPER) return access;

  const organisations =
    organisationUuid === creatingOrganisationUuid
      ? access.organisations.map((organisation) =>
          organisation.organisationUuid !== creatingOrganisationUuid &&
          organisation.existingDeveloperActions?.[projectKey]
            ? {
                ...organisation,
                existingDeveloperActions: { ...organisation.existingDeveloperActions, [projectKey]: "REMOVE" },
              }
            : organisation
        )
      : access.organisations.flatMap((organisation) =>
          organisation === removed ? withoutProjectKey(organisation, projectKey) : [organisation]
        );

  if (getResponsibleOwnerUuid(projectKey, access.responsibleOwners, creatingOrganisationUuid) !== organisationUuid) {
    return { ...access, organisations };
  }

  // REQ-023 / BR-02871 - the designation cannot stay with an organisation that no longer holds
  // access, so it falls to the developer, the one holder the project is guaranteed to keep.
  const fullDeveloper = getCurrentFullDeveloper(organisations, projectKey);

  return {
    organisations,
    responsibleOwners: {
      ...access.responsibleOwners,
      [projectKey]: fullDeveloper?.organisationUuid ?? creatingOrganisationUuid,
    },
  };
};

export type ExistingDeveloperPrompt = { projectKey: string; developerName: string };

// Which of the selected projects already list a full-access Developer that a Developer/Full-access
// add would displace - whichever organisation holds the role, the creator's own entry included. The
// api only lets that add past BR-02726 if it says what becomes of the outgoing developer (BR-02873).
// Only the organisation being added is excluded: it displaces nobody by holding the role itself, and
// re-adding it to the same project is refused as a duplicate anyway (findDuplicateProjectKeys).
// What the answer then does depends on whose access it is, which is applyExistingDeveloperActions'
// business rather than this one's.
export const getProjectsNeedingExistingDeveloperAction = (
  projectKeys: string[],
  organisations: AddedOrganisationAssignment[],
  addedOrganisationUuid: string | null
): ExistingDeveloperPrompt[] =>
  projectKeys.flatMap((projectKey) => {
    const current = getCurrentFullDeveloper(organisations, projectKey);
    if (!current || current.organisationUuid === addedOrganisationUuid) return [];

    return [{ projectKey, developerName: current.organisationName }];
  });

// The displaced add stops being a Developer add on that one project: dropped for REMOVE, or moved
// into that organisation's entry for the guest role it keeps. Its other projects are untouched,
// which is why the downgrade splits the entry rather than changing its role outright.
const rewriteDisplacedAdd = (
  organisations: AddedOrganisationAssignment[],
  displaced: AddedOrganisationAssignment,
  projectKey: string,
  action: string
): AddedOrganisationAssignment[] => {
  const remainingRole = roleAfterExistingDeveloperAction(action);
  const trimmed = organisations.flatMap((organisation) =>
    organisation === displaced ? withoutProjectKey(organisation, projectKey) : [organisation]
  );

  if (!remainingRole) return trimmed;

  const existingEntry = trimmed.find(
    (organisation) =>
      organisation.organisationUuid === displaced.organisationUuid && organisation.role === remainingRole
  );

  if (existingEntry) {
    return trimmed.map((organisation) =>
      organisation === existingEntry
        ? { ...organisation, projectKeys: [...organisation.projectKeys, projectKey] }
        : organisation
    );
  }

  return [
    ...trimmed,
    {
      ...displaced,
      // A fresh key rather than one built from the list's length: an entry removed and another
      // added would otherwise land on the same key and confuse React's reconciliation.
      key: uuidv4(),
      role: remainingRole,
      accessLabel: accessLabelForRole(remainingRole),
      projectKeys: [projectKey],
      existingDeveloperActions: {},
    },
  ];
};

export type AppliedExistingDeveloperActions = {
  organisations: AddedOrganisationAssignment[];
  responsibleOwners: Record<string, string>;
  existingDeveloperActions: Record<string, string | null>;
};

/**
 * Applies the answers the add card collected, per project: what the access list and the
 * responsible owners become, and the actions the incoming assignment carries to the api.
 *
 * The two developers an add can displace are not the same thing. The creating organisation holds
 * real access from creating the project (REQ-003), so its swap is the api's to perform: the action
 * rides the invite and is applied when that invite is accepted (BR-02873), with syncCreatingOrganisation
 * reading it back for the table. Any other organisation holding the role here holds nothing yet -
 * only a staged add - and BR-02878 lets the batch carry one Developer assignment per project
 * (ValidateSingleFullDeveloperPerProjectInBatch refuses two whatever action they declare), so that
 * one is rewritten in place instead. Whatever it had recorded against the creating organisation
 * moves onto the incoming add, which is now the assignment that performs that swap.
 *
 * Either way the designation follows the role: an organisation that is losing full access cannot
 * keep it (REQ-023 / BR-02871), so where the displaced developer held it, it moves to the one
 * taking over.
 */
export const applyExistingDeveloperActions = (
  access: ProjectAccessState,
  add: { organisationUuid: string; projectKeys: string[]; existingDeveloperActions: Record<string, string | null> },
  creatingOrganisationUuid: string
): AppliedExistingDeveloperActions =>
  add.projectKeys.reduce<AppliedExistingDeveloperActions>(
    (result, projectKey) => {
      const action = add.existingDeveloperActions[projectKey];
      if (!action) return result;

      const displaced = getCurrentFullDeveloper(result.organisations, projectKey);
      if (!displaced) return result;

      // Recorded explicitly even where it was only the REQ-003 default, because that default still
      // resolves to the creating organisation after a swap has taken its full access away. It is
      // also what puts makeResponsibleOwner on the invite, which is what the api demands of a swap
      // displacing the owner (BR-02886, ValidateResponsibleOwnerReassignedOnDeveloperSwap).
      const responsibleOwners =
        getResponsibleOwnerUuid(projectKey, result.responsibleOwners, creatingOrganisationUuid) ===
        displaced.organisationUuid
          ? { ...result.responsibleOwners, [projectKey]: add.organisationUuid }
          : result.responsibleOwners;

      if (displaced.organisationUuid === creatingOrganisationUuid) {
        return {
          ...result,
          responsibleOwners,
          existingDeveloperActions: { ...result.existingDeveloperActions, [projectKey]: action },
        };
      }

      const inheritedAction = displaced.existingDeveloperActions?.[projectKey];

      return {
        organisations: rewriteDisplacedAdd(result.organisations, displaced, projectKey, action),
        responsibleOwners,
        existingDeveloperActions: inheritedAction
          ? { ...result.existingDeveloperActions, [projectKey]: inheritedAction }
          : result.existingDeveloperActions,
      };
    },
    {
      organisations: access.organisations,
      responsibleOwners: access.responsibleOwners,
      existingDeveloperActions: {},
    }
  );

// The api rejects a repeated (organisation, project) pair for the whole batch, so the same pair is
// refused at add time rather than after the projects have already been created.
export const findDuplicateProjectKeys = (
  organisations: AddedOrganisationAssignment[],
  organisationUuid: string,
  projectKeys: string[]
): string[] =>
  projectKeys.filter((projectKey) =>
    organisations.some(
      (organisation) =>
        organisation.organisationUuid === organisationUuid && organisation.projectKeys.includes(projectKey)
    )
  );

// BR-02884 - one action produces one assignment per (organisation, project); the api consolidates
// them into a single notification per target organisation.
//
// Every organisation on the list is invited, including the incoming full-access Developer: the
// create puts only the creating organisation on the project, so every other link - that one
// included - is written when its invitation is accepted.
//
// The creating organisation is never invited, to any role. The create has already put it on the
// project, so the api would refuse the invitation outright
// (ValidateOrganisationNotAssociatedWithProject), and it does not need one: where a swap has left
// it holding a guest role, the incoming Developer's assignment carries the existingDeveloperAction
// that moves it there when that invitation is accepted (BR-02873).
export const buildPlannedInvites = (
  organisations: AddedOrganisationAssignment[],
  responsibleOwners: Record<string, string>,
  projectUuidByKey: Record<string, string>,
  projectNameByKey: Record<string, string>,
  creatingOrganisationUuid: string
): PlannedInvite[] =>
  organisations
    .filter((organisation) => organisation.organisationUuid !== creatingOrganisationUuid)
    .flatMap((organisation) =>
      organisation.projectKeys
        .filter((projectKey) => !!projectUuidByKey[projectKey])
        .map((projectKey) => ({
          organisationName: organisation.organisationName,
          projectName: projectNameByKey[projectKey] ?? "",
          assignment: {
            projectUuid: projectUuidByKey[projectKey],
            role: organisation.role,
            targetOrganisationUuid: organisation.organisationUuid,
            type: "Invitation",
            // REQ-023 - the designation rides the invite and transfers when it is accepted. Left off
            // when the creating organisation keeps the role, which needs no action at all.
            makeResponsibleOwner:
              getResponsibleOwnerUuid(projectKey, responsibleOwners, creatingOrganisationUuid) ===
              organisation.organisationUuid,
            existingDeveloperAction: organisation.existingDeveloperActions?.[projectKey] ?? null,
          },
        }))
    );

const assignmentIndexPattern = /Assignments\[(\d+)\]/i;

// A timeout or a 5xx with no JSON error body reaches this Status.Error with an empty errors array
// (same failure submitProjectDetails.ts guards on the create side) - the projects it invites for
// are already created, so this must read as a failure to save rather than show nothing at all.
const fallbackBulkInviteError: ServiceError = {
  code: "PROJECT_ACCESS_INVITE_FAILED",
  message: "Something went wrong and your invitations could not be sent. Please try again.",
};

// BR-02883 - the batch is rejected as a whole and every error is pathed Assignments[index], so each
// one is relabelled with the organisation and project that produced it.
export const describeBulkInviteErrors = (errors: ServiceError[] | undefined, plan: PlannedInvite[]): ServiceError[] => {
  if (!errors || errors.length === 0) return [fallbackBulkInviteError];

  return errors.map((error) => {
    const match = assignmentIndexPattern.exec(error.path ?? error.property ?? "");
    const planned = match ? plan[Number(match[1])] : undefined;
    const message = getErrorMessageFromCode(error.code, error.parameters ?? { property: error.property });

    return {
      ...error,
      message: planned ? `${planned.organisationName} on ${planned.projectName}: ${message}` : message,
    };
  });
};
